import React, { useContext, useState } from 'react';
import { useUser } from '../../contextManager/user.context';
import { useToast } from '../../contextManager/toast.context';
import useAuth from '../../hooks/auth.service';
import { ProfilePhotoCard, CardHeader, CardContent } from '../../styles/profile/layout.style';
import { useNavigate } from 'react-router-dom';
import { AxiosContext } from '../../contextManager/axios.context';
import { endpoints } from '../../constants/endpoints';

export const SettingsSection: React.FC = () => {
  const { userDetails } = useUser();
  const { patchProfile, logoutUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [discoverable, setDiscoverable] = useState(userDetails?.discoverable !== false);
  const [savingDiscoverable, setSavingDiscoverable] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { updateToast } = useToast();

  const handleToggleDiscoverable = async () => {
    const newVal = !discoverable;
    setSavingDiscoverable(true);
    try {
      await patchProfile(
        { discoverable: newVal } as any,
        {
          loading: newVal ? 'Making profile discoverable...' : 'Hiding profile from search...',
          success: newVal ? 'Profile is now discoverable.' : 'Profile hidden from search.',
        },
      );
      setDiscoverable(newVal);
    } catch {
      // Error toast handled inside patchProfile
    } finally {
      setSavingDiscoverable(false);
    }
  };

  const axios = useContext(AxiosContext);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    const toastId = `delete-account-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Deactivating account...' });
    try {
      await axios.post(endpoints.deactivateAccount);
      updateToast(toastId, { variant: 'success', title: 'Account deactivated.' });
      logoutUser();
      navigate('/');
    } catch {
      updateToast(toastId, { variant: 'error', title: 'Could not deactivate account.' });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div style={{ alignSelf: 'start', maxWidth: '450px' }}>
      <ProfilePhotoCard>
        <CardHeader>Settings</CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gap: '1.25rem', fontSize: '0.85rem' }}>

            {/* Discoverability */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>Profile Discoverability</h4>
                  <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.4, fontSize: '0.8rem' }}>
                    {discoverable
                      ? 'Your profile is visible in search results.'
                      : 'Your profile is hidden from search results.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleDiscoverable}
                  disabled={savingDiscoverable}
                  style={{
                    all: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: discoverable ? 'var(--colors-blue9)' : 'var(--colors-sand7)',
                    cursor: savingDiscoverable ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                    flexShrink: 0,
                    marginLeft: '1rem',
                    position: 'relative',
                  }}
                >
                  <div style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    left: discoverable ? 22 : 4,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid var(--colors-sand5)', margin: '0' }} />

            {/* Delete Account */}
            <div>
              <h4 style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#dc2626' }}>Delete Account</h4>
              <p style={{ margin: '0 0 0.75rem', color: 'var(--colors-sand11)', lineHeight: 1.4, fontSize: '0.8rem' }}>
                This will deactivate your account and hide your profile. Your data will be retained for 30 days before permanent deletion.
              </p>
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  style={{
                    all: 'unset',
                    padding: '0.4rem 0.75rem',
                    fontSize: '0.8rem',
                    borderRadius: 6,
                    border: '1px solid #fca5a5',
                    color: '#dc2626',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  Delete my account
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#dc2626' }}>Are you sure?</span>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    style={{
                      all: 'unset',
                      padding: '0.35rem 0.6rem',
                      fontSize: '0.8rem',
                      borderRadius: 6,
                      background: '#dc2626',
                      color: 'white',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      opacity: deleting ? 0.6 : 1,
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Yes, delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    style={{
                      all: 'unset',
                      padding: '0.35rem 0.6rem',
                      fontSize: '0.8rem',
                      borderRadius: 6,
                      border: '1px solid var(--colors-sand11)',
                      cursor: 'pointer',
                    }}
                  >
                    No
                  </button>
                </div>
              )}
            </div>

          </div>
        </CardContent>
      </ProfilePhotoCard>
    </div>
  );
};
